import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import reportService from '@api/v1/reportService'
import exagoReportService from '@api/v1/exagoReportService'
import searchService from '@api/v1/searchService'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'
import useDocumentTitle from '@hooks/useDocumentTitle'
import { SMART_SEARCH } from '@config/featureFlags'
import { useDashboardsTab } from '@hooks/useUserSettings'
import DashboardsPanel from '@components/dashboards/DashboardsPanel'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'

const ALL_TAB = { id: 'all', name: 'All Reports' }
const NO_TABS = []

// Spec 190 (CC-54303): the firm's Exago ("Advanced") reports that Report Center can run
// join the catalog as one more category. cb-api's allowlist decides which firm gets any
// (Kucker Marino only today) and answers every other firm with an empty list without
// asking CB, so for them this category never exists. Cards route to
// /reports/exago/:id (App.jsx), never to the generic /reports/:reportId engine.
const EXAGO_TAB_ID = 'advanced'

/**
 * Spec 198: the sentence for why this user has no Advanced Reports listed. Who may open
 * them is CB's rule, the same at every firm: Reports → "Can Access Exago Reports" on the
 * user's own security profile. CB does not exempt a firm administrator, so an
 * administrator is told that in so many words — they can turn it on for themselves.
 */
const exagoNoticeText = (reason, { admin, error }) => {
  switch (reason) {
    case 'exago-permission':
      return admin
        ? 'You’re a firm administrator, but Centerbase opens Advanced Reports only for accounts whose security profile has Reports → “Can Access Exago Reports”, and yours doesn’t have it yet. Turn it on for your profile in Centerbase, then refresh this page.'
        : 'Your Centerbase account can’t open your firm’s Advanced Reports yet: your security profile needs Reports → “Can Access Exago Reports”. Ask your Centerbase administrator to turn it on.'
    case 'not-listed':
      return 'Your firm’s Advanced Report isn’t shared with your account in Exago, so it isn’t listed here.'
    case 'unavailable':
      return 'Your firm’s Advanced Reports could not be loaded right now.'
    default:
      return `Advanced Reports could not be loaded${error?.message ? ` (${error.message})` : ''}.`
  }
}

// Spec 209: the Advanced Reports tab's empty state when the generic list for this user is
// read and empty — an answer, not a search that matched nothing. It says what REPORT CENTER
// offers, never what Centerbase lists: cb-api answers this same empty shape when CB did list
// items it skips (dashboards, chained reports, non-GUID ids), so "Centerbase lists none"
// could be false. This sentence is true in both states.
const EXAGO_NONE_LISTED = 'No Advanced Reports are available to you in Report Center.'

// Spec 209 §10: a generic report has no reviewed summary, so its card says where it lives
// in Exago — the folder, without the "Public\" every shared folder starts with.
const exagoFolderSummary = (folder) => {
  if (typeof folder !== 'string') return null
  const shown = folder.replace(/^Public\\/i, '').trim()
  return shown && shown.toLowerCase() !== 'public' ? shown : null
}

// Spec 209 §3: a GENERIC list's reports are the firm's own — name and Exago folder — so their
// cards carry PENDO_IGNORE (`firmAuthored`). Only the reviewed report (spec 190) is ours to
// name; anything a generic list does not mark `reviewed: true` is treated as the firm's.
const exagoTab = (reports, { generic = false } = {}) => ({
  id: EXAGO_TAB_ID,
  name: 'Advanced Reports',
  summary: 'Your firm’s Advanced Reports, run in Exago',
  reports: reports.map((r) => ({
    id: `exago/${r.id}`,
    name: r.name,
    summary: r.summary || exagoFolderSummary(r.folder),
    ...(generic && r.reviewed !== true ? { firmAuthored: true } : {}),
  })),
})

// Layer B: for a matched entity, the type-appropriate reports to offer (opened
// pre-filtered to it). ONLY list reports that actually have a matter/client
// LOOKUP filter column for ReportView to auto-prefill — otherwise every entity
// opens the same unfiltered report (Aged A/R is specialized, prefilled in
// ARReport). NB: "Matter Snapshot" is a firm-wide portfolio report with only
// date/posting filters (no per-matter filter), so it is intentionally NOT here.
const REPORTS_FOR_TYPE = {
  matter: ['Aged A/R', 'Aged WIP by Matter', 'Outstanding A/R'],
  contact: ['Aged A/R', 'Aged WIP by Matter', 'Outstanding A/R'],
}
const entityTypeLabel = { matter: 'matter', contact: 'client' }

/** Pick a thematic icon based on the report name. CB doesn't carry icon metadata. */
const iconFor = (name = '') => {
  const n = name.toLowerCase()
  if (/(receivable|payable|payment|invoice|bill|cash|trust)/.test(n)) return 'currency'
  if (/(time|hour|productivity)/.test(n)) return 'clock'
  if (/(realization|chart|metric|analytic)/.test(n)) return 'chart'
  if (/(attorney|user|staff|origination|fee allocation|commission)/.test(n)) return 'users'
  if (/(calendar|activity|appointment|task)/.test(n)) return 'calendar'
  return 'document'
}

// Color-code report categories with the brand pillar colors (blue/purple/green/teal —
// orange stays reserved for CTAs). Icon + label share a hue; label text uses the
// AA-safe -dark/accent-text tone. A stable hash keeps each category on one color.
const CATEGORY_PALETTE = [
  { icon: 'bg-cb-accent/10 text-cb-accent-text', pill: 'bg-cb-accent/10 text-cb-accent-text' },
  { icon: 'bg-cb-purple/15 text-cb-purple-dark', pill: 'bg-cb-purple/10 text-cb-purple-dark' },
  { icon: 'bg-cb-green/15 text-cb-green-dark', pill: 'bg-cb-green/10 text-cb-green-dark' },
  { icon: 'bg-cb-teal/15 text-cb-teal-dark', pill: 'bg-cb-teal/10 text-cb-teal-dark' },
]
const categoryPalette = (name = '') => {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return CATEGORY_PALETTE[h % CATEGORY_PALETTE.length]
}

const ICONS = {
  currency: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  clock: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  chart: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  document: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  users: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  ),
  calendar: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  ),
}

const Reports = ({ linkPrefix = '/reports' } = {}) => {
  useDocumentTitle('Report Center')
  const navigate = useNavigate()
  // Search is driven by the header's report-search box via the ?q= URL param
  // (single source of truth; shareable + survives a card click → back).
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') || ''
  // CC-49903: the selected category lives in the URL (?tab=) so opening a
  // report and coming BACK (browser back or the report pages' back arrow via
  // useBackNav) lands on the same category instead of "All reports".
  const activeTab = searchParams.get('tab') || 'all'
  const setActiveTab = (id) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev)
    if (String(id) === 'all') p.delete('tab')
    else p.set('tab', String(id))
    return p
  }, { replace: true })
  // The app header owns the visible "Report Center." title; the catalog keeps an
  // sr-only h1 for a11y. In the header-less IQ embed, show the title visibly.
  const inEmbed = linkPrefix.startsWith('/embed')

  // Dashboards tab (spec 038): a "Dashboards | Reports" view toggle, all URL-driven
  // (?mode=reports, ?d=<dashboardId>) so it's shareable + survives back/forward.
  // Dashboards is the DEFAULT view when enabled → the effective landing page.
  // Gated by BOTH the build availability flag (DASHBOARDS_TAB — where the feature
  // is shipped) AND a per-user opt-in (Settings → Dashboards; server-persisted).
  // Both layers now live in useDashboardsTab(), so the Queue and Schedules
  // pages resolve the tab exactly the way this page does.
  const dashboardsEnabled = useDashboardsTab() && !inEmbed
  // Typing in the header report-search (?q=) means the user wants reports — auto-
  // switch out of Dashboards so results are actually visible. Clearing it (or
  // clicking Dashboards) returns to the dashboards view.
  const hasQuery = (searchParams.get('q') || '').trim().length > 0
  const mode = dashboardsEnabled && searchParams.get('mode') !== 'reports' && !hasQuery ? 'dashboards' : 'reports'
  const selectedDashboard = searchParams.get('d')

  // Queue tab badge = work the user would want to act on (running + ready).
  const setMode = (m) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev)
    if (m === 'reports') { p.set('mode', 'reports') }
    else { p.delete('mode'); p.delete('q') } // back to Dashboards → drop any search
    p.delete('d')
    return p
  }, { replace: true })
  const openDashboard = (id) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev); p.set('d', String(id)); return p
  })

  const catalogQuery = useQuery({
    queryKey: ['reports-list'],
    queryFn: () => reportService.list(''),
    staleTime: 60_000,
  })
  const { data: catalogTabs = NO_TABS, error, refetch } = catalogQuery

  // Spec 190: not in the IQ embed, which has no route for these.
  const exagoQuery = useQuery({
    queryKey: ['exago-reports'],
    queryFn: () => exagoReportService.list(),
    enabled: !inEmbed,
    staleTime: 60_000,
  })
  const exagoReports = exagoQuery.data?.reports
  const exagoUnavailable = exagoQuery.data?.unavailable === true
  const exagoBlocked = exagoQuery.data?.blocked ?? null
  // Spec 198: cb-api marks a firm that HAS Advanced Reports in Report Center (`configured`).
  // Its category then shows to every user there — empty, with the reason at the top of the
  // page, when this user cannot open them. Leaving the category out read as "your firm has
  // none", and the reason sat below ~140 report cards, where Kucker Marino's firm admin
  // never saw it (2026-10-16, and again 2026-10-19).
  const exagoConfigured = exagoQuery.data?.configured === true
  const exagoAdmin = exagoQuery.data?.admin === true
  const exagoError = exagoQuery.error
  const exagoNotice = exagoError
    ? 'error'
    : (exagoConfigured && !exagoReports?.length ? (exagoBlocked || (exagoUnavailable ? 'unavailable' : null)) : null)
  // FIRST, not last (spec 190 §9): appended after ~20 CB categories, the firm's own
  // Advanced Report was the last chip and the last of ~200 cards in "All Reports", and a
  // firm admin looking for it did not find it.
  // Spec 209 §10: a GENERIC list (every report in the user's Advanced Reports — 16 stock ones
  // on vladdemo, hundreds at a firm that designs its own) goes AFTER the catalog instead, so
  // smart search and `/suggestions`' 200-entry window see CB's catalog first. A firm with
  // only the reviewed report keeps it first, as above.
  const exagoGeneric = exagoQuery.data?.generic === true
  // Spec 209: a generic list that was READ and holds nothing — supported site, configured,
  // no `blocked` reason, not `unavailable`, no error — is the answer for this user, not a
  // search that matched nothing. Its empty tab says that (EXAGO_NONE_LISTED), never
  // "No reports match your search" with nothing searched. `!exagoNotice` is load-bearing:
  // a whitespace-only search skips the notice branch below and reaches this one.
  const exagoNoneListed = exagoGeneric && exagoConfigured && !exagoNotice
    && Array.isArray(exagoReports) && exagoReports.length === 0
  const tabs = useMemo(() => {
    if (!(exagoReports?.length || exagoConfigured)) return catalogTabs
    const tab = exagoTab(exagoReports || [], { generic: exagoGeneric })
    return exagoGeneric ? [...catalogTabs, tab] : [tab, ...catalogTabs]
  }, [catalogTabs, exagoReports, exagoConfigured, exagoGeneric])

  /*
   * Three states, not two — see `@api/v1/readState`.
   *
   * This used to read `isLoading`, which is `isPending && isFetching` and so is
   * FALSE for a query that is pending but not in flight. When react-query pauses
   * the query (default `networkMode: 'online'`, `onlineManager` believing the
   * browser is offline) `queryFn` is never called, nothing throws, nothing logs,
   * and `data` stays undefined — so `tabs` fell back to `[]` and this page stated
   * "No reports match your search" with a count of **0**, indefinitely.
   *
   * Measured on the deployed QA host 2026-08-21: paused query, `queryFn` calls =
   * 0, no spinner, no error, "All Reports 0". Meanwhile the endpoint itself
   * returned 200 with all 140 reports on every one of 20 hand-issued requests, so
   * every server-side investigation of this came back clean. That is the whole
   * reason it survived: the failing load makes NO request to find in a log.
   */
  const catalogState = useReadState('reports-list', catalogQuery)
  const catalogReady = catalogState === 'ready'

  // Build a flat report list with their tab context for "All" view + filtering.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    const visible = activeTab === 'all'
      ? tabs.flatMap((t) => t.reports.map((r) => ({ ...r, tabName: t.name })))
      : (tabs.find((t) => String(t.id) === String(activeTab))?.reports || [])
          .map((r) => ({ ...r, tabName: tabs.find((t) => String(t.id) === String(activeTab))?.name }))
    if (!term) return visible
    return visible.filter(
      (r) => r.name?.toLowerCase().includes(term) || r.summary?.toLowerCase().includes(term)
    )
  }, [tabs, activeTab, search])

  // Smart search: debounce the query, then ask Workers AI to rank the catalog.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500)
    return () => clearTimeout(t)
  }, [search])

  // Dedupe by name — CB lists the same report under multiple category tabs, which
  // would otherwise surface duplicate suggestion chips (e.g. "Aged A/R" twice).
  const catalog = useMemo(() => {
    const byName = new Map()
    for (const t of tabs) {
      for (const r of t.reports || []) {
        if (r?.name && !byName.has(r.name)) byName.set(r.name, { id: r.id, name: r.name, summary: r.summary })
      }
    }
    return [...byName.values()]
  }, [tabs])

  // Layer B: report name → id, to resolve the type-appropriate reports per entity.
  const reportIdByName = useMemo(() => {
    const m = new Map()
    for (const t of tabs) {
      for (const r of t.reports || []) {
        if (r?.name && !m.has(r.name)) m.set(r.name, r.id)
      }
    }
    return m
  }, [tabs])
  const { data: suggestions = [], isFetching: suggFetching } = useQuery({
    queryKey: ['report-suggestions', debouncedSearch],
    queryFn: () => searchService.reportSuggestions({ query: debouncedSearch, reports: catalog }),
    enabled: SMART_SEARCH && !inEmbed && debouncedSearch.trim().length >= 3 && catalog.length > 0,
    staleTime: 5 * 60_000,
  })

  // Layer B: universal search for real entities (matters/clients) matching the query.
  const { data: entities = [], isFetching: entFetching } = useQuery({
    queryKey: ['report-entities', debouncedSearch],
    queryFn: () => searchService.quickSearch(debouncedSearch),
    enabled: SMART_SEARCH && !inEmbed && debouncedSearch.trim().length >= 3,
    staleTime: 60_000,
  })

  // AI is "busy" while the user is typing (debounce still catching up) or a query is
  // in flight — drives the loading indicators so the strips don't just pop in silently.
  const searching = SMART_SEARCH && !inEmbed && search.trim().length >= 3
  const debouncePending = debouncedSearch !== search
  const suggBusy = searching && (debouncePending || suggFetching)
  const entBusy = searching && (debouncePending || entFetching)

  const totalCount = useMemo(
    () => tabs.reduce((sum, t) => sum + (t.reports?.length || 0), 0),
    [tabs]
  )

  return (
    /* Centered, focused landing (report views stay full-width for wide tables). */
    <div className="report-shell">
      {/* In-app, the header owns the pillar dots + "Report Center." title, so the
          catalog goes straight to the tabs (more room for reports). The header-less
          IQ embed still shows the hero here. */}
      {inEmbed ? (
        <div className="mb-8 text-center">
          <div className="flex justify-center gap-1.5 mb-4" aria-hidden="true">
            <span className="w-1.5 h-1.5 rounded-full bg-cb-orange" />
            <span className="w-1.5 h-1.5 rounded-full bg-cb-green" />
            <span className="w-1.5 h-1.5 rounded-full bg-cb-purple" />
            <span className="w-1.5 h-1.5 rounded-full bg-cb-teal" />
          </div>
          <h1 className="text-4xl font-display text-cb-navy tracking-tight">
            Report Center<span aria-hidden="true" className="text-cb-orange">.</span>
          </h1>
        </div>
      ) : (
        /*
         * The catalog's own title block (Figma Row B, `110:9246`): title and
         * subtitle on the left, the count right-aligned on the SAME row.
         *
         * This used to be an `sr-only` h1, on the grounds that the header owned
         * the visible "Report Center." title. The mock gives the page a real
         * heading — and now that the header also carries a "Reports" nav tab,
         * "Report Center" was about to appear twice on one screen. So the page
         * says what it is ("Reports") and the header keeps the brand.
         *
         * The count moved up here from a row of its own below the pills, which is
         * where the mock puts it and one row less of chrome.
         */
        /*
         * The heading follows the VIEW, and the count only appears where it
         * describes what is on screen.
         *
         * First cut rendered this block unconditionally, so the Dashboards panel
         * also got a heading reading "Reports" and a "N reports · M categories"
         * line that had nothing to do with the dashboard below it. The dashboards
         * suite's DOM dump showed it; looking at the page did not.
         *
         * Both views keep an h1 — dropping it on Dashboards would leave that view
         * with no page heading at all, which the ADA gate flags and a screen
         * reader would notice before anyone else did.
         */
        <div className="mb-5 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="font-display text-2xl leading-tight text-rc-ink">
              {mode === 'dashboards' ? 'Dashboards' : 'Reports'}
            </h1>
            <p className="mt-0.5 text-[13px] text-rc-muted">
              {mode === 'dashboards'
                ? 'Your firm’s dashboards, at a glance.'
                : 'Browse, filter and run any of your firm’s reports.'}
            </p>
          </div>
          {mode !== 'dashboards' && catalogReady && (
            <span className="text-xs text-rc-muted" aria-live="polite">
              {search ? (
                <>
                  Showing results for{' '}
                  <span className="font-semibold text-rc-ink">“{search}”</span>
                </>
              ) : totalCount > 0 ? (
                <>
                  {totalCount} report{totalCount === 1 ? '' : 's'} · {tabs.length} categories
                </>
              ) : null}
            </span>
          )}
        </div>
      )}

      {/* Dashboards | Reports | Queue moved into the TOP BAR (ReportCenterNav,
          mounted by Header) — the Figma places it there, and three page-level
          copies of one nav had drifted apart.

          The EMBED keeps its own copy: `/embed/reports` renders this page
          without the app shell, so there is no header to host the nav, and the
          embed is deliberately chromeless anyway. `inEmbed` already suppressed
          the group entirely, so the embed loses nothing here. */}

      {mode === 'dashboards' ? (
        <DashboardsPanel
          selectedId={selectedDashboard}
          onSelect={openDashboard}
          onBrowseReports={() => setMode('reports')}
        />
      ) : (
      <>

      {/* Category filter. LEFT-aligned per the mock (Row B) — centring a strip
          that wraps to three lines on 16 categories reads as an accident, and the
          title above it is left-aligned too. Search lives in the header.

          Gated on a COMPLETED read, like every other count on this page: the pills
          carry per-category counts and the "All Reports" pill carries the total, so
          an unresolved read rendered a lone pill reading "All Reports 0". Its own
          test caught that after the empty-state and title-row counts were already
          fixed — three separate places on one page assert a number. */}
      {catalogReady && (
      <div className="mb-4 animate-fade-up" style={{ animationDelay: '60ms' }}>
        {/* Wrap the category pills so every category stays visible — with 15+
            categories a single scroll strip hides most of them off-screen.

            NO grey track here, deliberately. This used to sit on
            `rounded-rc-card bg-gray-100 p-1.5`, borrowed from the header's segmented
            control. A segmented control is a fixed set of 2-3 mutually exclusive
            views; this is 16 filter chips that wrap to three rows. A wrapping flex
            container cannot shrink to its widest laid-out row, so the track took the
            full page width and left a large empty grey area at the end of row one.

            `w-fit` does NOT fix it — that resolves to max-content, which is wider
            than the line, so the box is still the available width. Nine other pages
            carry `w-fit` on this pattern and get away with it only because their 2-5
            items never wrap. Dropping the track is the only option that looks right
            at 16 categories AND at two.

            "Selected" was defined as white-on-grey, so losing the track means the
            active chip needs a filled state of its own — hence the rc-chip tokens
            below, and an outline on the inactive ones so they still read as
            controls.

            STALE as of 2026-08-26 (Himaad UI feedback, B6): the clause above used
            to end "(SC 1.4.11: rc-line is a divider, rc-control on hover)". Both
            halves of that were defects, and both are fixed below.

            B6 — the selected state was "too weak", and the measurement says so:
            chip-bg #eff6ff against the gray-50 page ground (Layout.jsx:39) is a
            1.04:1 fill shift. At 13px that plus a hue change WAS the entire
            selected state. It now also carries a stroke and a weight step
            (medium -> semibold). Three cues, not one.

            The stroke is rc-chip-text (#1d4ed8), NOT rc-chip-line. rc-chip-line
            was tuned to 3.03:1 against WHITE, but this pill sits on the gray-50
            page ground and encloses a #eff6ff fill, where the same hex measures
            2.90:1 and 2.78:1 — under the SC 1.4.11 floor on both surfaces it has
            to delimit. rc-chip-text measures 6.41:1 and 6.16:1 there, and it is
            already the pill's own text and badge colour, so this is Himaad's
            "stay in the colour family and push darker" with no new hex.
            Measure a chip border against the PAGE and against its OWN FILL — a
            ratio quoted against white is not the ratio a chip actually gets.

            The border moved to the SHARED half of the class string. It used to be
            on the unselected branch only, so selecting a pill dropped 2px of box
            on both axes and re-flowed a 16-pill row that wraps to three lines.
            Only the border COLOUR changes per state now.

            Deliberately no transform/scale/lift on the selected state: motion is
            not a state cue (it is invisible to anyone with reduced-motion set, and
            it says "you are hovering", not "this is applied"). Same reason the
            fill alone was not enough.

            rc-control is the RESTING border, not a hover reward. Reaching 3.03:1
            only on :hover means a keyboard or touch user never saw a boundary at
            all — the control was identified by a 1.24:1 divider colour for them.
            Hover now moves to rc-muted, matching .btn-rc-pill:hover in index.css.

            Selected keeps rounded-full: these are chips, not buttons, so the B2
            6px control radius deliberately does not apply. */}
        {/* Spec 198: why this user has no Advanced Reports, ABOVE the categories, where it
            is seen on arrival. It used to be a grey line under every report card. */}
        {exagoNotice && (
          <div
            role="status"
            data-testid="exago-notice"
            data-reason={exagoNotice}
            className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            <p className="font-semibold">Advanced Reports</p>
            <p className="mt-1">{exagoNoticeText(exagoNotice, { admin: exagoAdmin, error: exagoError })}</p>
            {(exagoNotice === 'error' || exagoNotice === 'unavailable') && (
              <button
                type="button"
                onClick={() => exagoQuery.refetch()}
                className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                Try again
              </button>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {/* This pill and the mapped one below are the SAME control with different
              gates, and no test compares them — so any change here has to be made
              twice or the "All" pill silently drifts out of the set it belongs to.
              Keep the two class strings byte-identical apart from the gate name. */}
          <button
            key="all"
            onClick={() => setActiveTab('all')}
            aria-pressed={activeTab === 'all'}
            className={`px-3.5 py-1.5 rounded-full border text-[13px] whitespace-nowrap transition-colors flex items-center gap-1.5 ${
              activeTab === 'all' ? 'border-rc-chip-text bg-rc-chip-bg text-rc-chip-text font-semibold' : 'border-rc-control bg-white text-rc-muted font-medium hover:border-rc-muted hover:text-rc-ink-2'
            }`}
          >
            {ALL_TAB.name}
            {/* The count is part of the button's ACCESSIBLE NAME — five locators
                anchor on "{label}{space}{count}" (e.g. Reports.test.jsx:96
                /^All Reports\s*3$/, e2e/reports.spec.js:189). Restyle the span
                freely; never give it a separator, sr-only wording or an
                aria-label, and never move the text out of the button. */}
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
              activeTab === 'all' ? 'bg-white text-rc-chip-text' : 'bg-gray-200 text-rc-ink-2'
            }`}>
              {totalCount}
            </span>
          </button>
          {tabs.map((tab) => {
            const count = tab.reports?.length || 0
            const isActive = String(activeTab) === String(tab.id)
            return (
              /* Twin of the "All Reports" pill above — class strings kept identical. */
              <button
                key={tab.id}
                onClick={() => setActiveTab(String(tab.id))}
                aria-pressed={isActive}
                className={`px-3.5 py-1.5 rounded-full border text-[13px] whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  isActive ? 'border-rc-chip-text bg-rc-chip-bg text-rc-chip-text font-semibold' : 'border-rc-control bg-white text-rc-muted font-medium hover:border-rc-muted hover:text-rc-ink-2'
                }`}
              >
                {tab.name}
                {/* Accessible name = label + count (Reports.dashboards.test.jsx:70
                    /^Financial\s*1$/). See the note on the "All" pill above. */}
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-white text-rc-chip-text' : 'bg-gray-200 text-rc-ink-2'
                }`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      </div>
      )}

      {/* The count / search echo moved UP into the title row (see above), which
          is where the mock puts it — one row less of chrome above the grid. The
          embed has no title row, so it keeps the line. */}
      {inEmbed && catalogReady && (
        <div className="flex justify-end mb-3 min-h-[18px]">
          {search ? (
            <span className="text-xs text-gray-500">
              Showing results for <span className="font-semibold text-cb-navy">“{search}”</span>
            </span>
          ) : totalCount > 0 ? (
            <span className="text-xs text-gray-500">
              {totalCount} report{totalCount === 1 ? '' : 's'} · {tabs.length} categories
            </span>
          ) : null}
        </div>
      )}

      {/* Loading indicators while the AI ranks + universal search runs. */}
      {suggBusy && suggestions.length === 0 && (
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <span aria-hidden="true">✨</span> Smart suggestions
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-500" role="status" aria-live="polite">
            <span className="inline-block w-3.5 h-3.5 border-2 border-gray-300 border-t-cb-accent rounded-full animate-spin" aria-hidden="true" />
            Finding relevant reports…
          </div>
        </div>
      )}
      {entBusy && !entities.some((e) => REPORTS_FOR_TYPE[e.type]) && (
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
            In your data
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-500" role="status" aria-live="polite">
            <span className="inline-block w-3.5 h-3.5 border-2 border-gray-300 border-t-cb-green rounded-full animate-spin" aria-hidden="true" />
            Searching your matters &amp; clients…
          </div>
        </div>
      )}

      {/* Smart search — Workers-AI-ranked report suggestions for the typed query. */}
      {SMART_SEARCH && !inEmbed && suggestions.length > 0 && (
        <div className="mb-6 animate-fade-up">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <span aria-hidden="true">✨</span> Smart suggestions
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`${linkPrefix}/${s.id}`)}
                className="text-left bg-white border border-cb-accent/40 rounded-cb shadow-cb px-3.5 py-2 hover:border-cb-accent hover:shadow-cb-lg hover:-translate-y-0.5 transition-all"
              >
                <span className="block text-sm font-semibold text-cb-navy">{s.name}</span>
                {s.reason && <span className="block text-xs text-gray-500 mt-0.5">{s.reason}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Layer B: matched entities → open a type-appropriate report scoped to them.
          Independent of the report suggestions above (so name-only queries surface). */}
      {SMART_SEARCH && !inEmbed && entities.some((e) => REPORTS_FOR_TYPE[e.type]) && (
        <div className="mb-6 animate-fade-up">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
            In your data
          </p>
          <div className="flex flex-col gap-2">
            {entities
              .filter((e) => REPORTS_FOR_TYPE[e.type])
              .slice(0, 3)
              .map((e) => {
                const reports = (REPORTS_FOR_TYPE[e.type] || [])
                  .map((n) => ({ name: n, id: reportIdByName.get(n) }))
                  .filter((r) => r.id)
                  .slice(0, 4)
                if (reports.length === 0) return null
                return (
                  <div key={`${e.type}-${e.id}`} className="bg-white border border-gray-100 rounded-cb shadow-cb px-4 py-3">
                    <div className="text-sm font-semibold text-cb-navy">
                      {e.name} <span className="text-xs font-normal text-gray-500">· {entityTypeLabel[e.type] || e.type}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {reports.map((r) => (
                        <button
                          key={r.id}
                          onClick={() =>
                            navigate(
                              `${linkPrefix}/${r.id}?ei=${encodeURIComponent(e.id)}&en=${encodeURIComponent(e.name || '')}&et=${e.type}`
                            )
                          }
                          className="text-xs font-medium text-cb-navy bg-white border border-cb-green/50 rounded-full px-3 py-1 hover:bg-cb-green/10 hover:border-cb-green transition-colors"
                          title={`Open ${r.name} for ${e.name}`}
                        >
                          {r.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
          </div>
        </div>
      )}

      {/* `idle` rides with `loading` on purpose. It means pending-but-never-started
          (`enabled: false`), which this query cannot reach today — it has no
          `enabled` option. But the first version of this fix handled four states
          and left `idle` rendering NOTHING, i.e. a blank page: every branch below
          is gated on ready/blocked/failed, so an unhandled fifth state falls
          through all of them. Anyone adding an `enabled` gate here later would
          have gotten a silent blank screen as the reward. Cheaper to cover it
          than to leave the trap. */}
      {(catalogState === 'loading' || catalogState === 'idle') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 flex justify-center">
          <LoadingSpinner message="Loading reports..." />
        </div>
      )}

      {/* The state that used to render as "your firm has no reports".
          react-query PAUSES a query while it believes the browser is offline —
          `queryFn` is never called, so there is no request, no error and no
          spinner. A spinner forever would be almost as bad as the empty state, so
          say what is actually happening and offer the retry. `role="alert"` for
          the same reason the failure below has one: the user has to know the
          screen is not describing their data. */}
      {catalogState === 'blocked' && (
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Can’t reach the server, so we haven’t loaded your reports.</p>
          <p className="mt-1 text-amber-800">
            This is not an empty catalog — your connection looks offline. Check it and try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* CC-50321: a failed read gets an error state WITH a way out, not the
          "No reports match your search" empty state. The two are different facts
          and a user who sees the wrong one has no reason to retry — they conclude
          the firm has no reports. `role="alert"` so it is announced, and Try again
          refetches rather than making the user reload the app. */}
      {error && (
        <div role="alert" className="bg-red-50 border border-red-100 rounded-xl p-6 text-sm text-red-700">
          <p className="font-medium">Failed to load reports: {error.message}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      )}

      {catalogReady && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-up" style={{ animationDelay: '120ms' }}>
          {filtered.map((report) => {
            const icon = iconFor(report.name)
            const palette = categoryPalette(report.tabName || report.name)
            return (
              <button
                key={report.id}
                onClick={() => navigate(`${linkPrefix}/${report.id}`)}
                /* Spec 209 §3: a firm-authored Advanced Report's name and folder never
                   reach Pendo. Every other card's class string is unchanged. */
                className={`${report.firmAuthored ? `${PENDO_IGNORE} ` : ''}group cursor-pointer rounded-rc-card border border-rc-line bg-white p-4 text-left shadow-rc-card transition-all duration-200 hover:-translate-y-0.5 hover:border-rc-control hover:shadow-cb-lg focus-visible:-translate-y-0.5`}
              >
                <div className="flex items-start gap-3">
                  <div className={`rounded-rc-control p-2.5 ${palette.icon} transition-transform duration-200 group-hover:scale-105`}>{ICONS[icon]}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[13px] font-semibold text-rc-ink transition-colors group-hover:text-cb-accent-text">{report.name}</h3>
                    {report.summary && (
                      <p className="mt-1 line-clamp-2 text-xs text-rc-muted">{report.summary}</p>
                    )}
                    {report.tabName && (
                      <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide mt-2 px-1.5 py-0.5 rounded-full ${palette.pill}`}>
                        {report.tabName}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {catalogReady && filtered.length === 0 && (
        <div className="text-center py-16 text-sm text-gray-500">
          {String(activeTab) === EXAGO_TAB_ID && !search && exagoNotice
            ? 'No Advanced Reports to show — the note above says why.'
            : String(activeTab) === EXAGO_TAB_ID && !search.trim() && exagoNoneListed
              ? EXAGO_NONE_LISTED
              : 'No reports match your search'}
        </div>
      )}
      </>
      )}
    </div>
  )
}

export default Reports
